import cx from 'classnames';
import {useRef} from 'react';
import {useSelector} from 'react-redux';
import {useCanvasImage} from '~/common/canvas/hooks/useCanvasImage';
import {useNavigator} from '~/common/FabricInvitation/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {useOrientation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useOrientation';
import {selectUsedAs} from '~/common/fabric/reducers/eviteEvent/selectors';
import {isGreetingCardByUsedAs} from '~/common/utils/event_utils';
import {Button} from '~library/atoms/Button';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {CANVAS_ORIENTATION} from '../constants';
import {useCardVideo} from './hooks/useCardVideo';

import './Card.sass';

const CardImage = ({image, style, imageRef, redirectTo, loading, isMp4, orientation}) => {
  return (
    <img
      id="fabric-card"
      src={image}
      className={cx(
        'fabric_user_card',
        {
          'fabric_user_card--clickable': redirectTo,
          'envelope_image__wrapper--clickable--disabled': redirectTo && loading,
          'fabric_user_card--mp4': isMp4,
        },
        'greeting-card-flow',
        `fabric-card--${orientation}`
      )}
      style={style}
      ref={imageRef}
      alt=""
    />
  );
};

const BackgroundVideo = ({src, isFree, isLargishAndUp, templateDimension}) => (
  <video
    className={cx('fabric_user_card--mp4_background', {
      // biome-ignore lint/style/useNamingConvention: camel case
      fabric_user_card: !isFree && isLargishAndUp,
    })}
    autoPlay
    loop
    muted
    playsInline
    style={templateDimension}
  >
    <source src={src} type="video/mp4" />
  </video>
);

const UYOVideo = ({uyoVideo, frameImage, videoStyles, videoRef}) => (
  <div className="fabric_user_card--mp4_background" ref={videoRef}>
    {frameImage && (
      <img className="fabric_user_card--mp4_background__frame" src={frameImage.src} alt="" />
    )}
    <video className="fabric-user-card__video" autoPlay loop muted playsInline style={videoStyles}>
      <source src={uyoVideo.src} type="video/mp4" />
    </video>
  </div>
);

export const Card = ({
  className = '',
  fabricArt,
  imageRef,
  redirectTo,
  isFree = false,
  parentNode,
}) => {
  const cardWrapperRef = useRef(null);
  const videoRef = useRef(null);
  const {image, loading} = useCanvasImage(fabricArt);
  const isLargishAndUp = useIsLargishAndUp();
  const usedAs = useSelector(selectUsedAs);
  const isGreetingCard = isGreetingCardByUsedAs(usedAs);
  const isMobile = !isLargishAndUp;
  const device = isMobile ? 'mobile' : 'desktop';

  const {videoStyles, isMp4, isGif, uyoVideo, frameImage, backgroundImage} = useCardVideo(
    fabricArt,
    videoRef
  );

  const orientation = useOrientation();

  const navigate = useNavigator({
    isMobile,
    disableAutoOpenEditor: redirectTo === Sections.CARD_DESIGN,
  });

  const getOrientation = CANVAS_ORIENTATION[device][orientation];
  const templateDimension = !isGreetingCard
    ? {width: getOrientation[0], height: getOrientation[1]}
    : {};
  const style = {
    ...templateDimension,
    ...(isGif &&
      backgroundImage?.src && {
        backgroundImage: `url(${backgroundImage?.src})`,
        backgroundSize: '100%',
        backgroundRepeat: 'no-repeat',
      }),
  };

  return (
    <>
      {loading && <span className="fabric_user_card-spinner evt-spinner inline" />}
      <Button
        data-qa-id="card-img"
        variant="transparent"
        onClick={!loading && redirectTo ? () => navigate(redirectTo) : undefined}
        className={cx(className, {
          'container--mp4': isMp4,
        })}
        ref={cardWrapperRef}
        ariaLabel="event invitation image"
      >
        <CardImage
          image={image}
          style={style}
          imageRef={imageRef}
          redirectTo={redirectTo}
          loading={loading}
          isMp4={isMp4}
          orientation={orientation}
        />
        {isMp4 && backgroundImage?.src && (
          <BackgroundVideo
            src={backgroundImage.src}
            isFree={isFree}
            isLargishAndUp={isLargishAndUp}
            templateDimension={templateDimension}
          />
        )}
        {uyoVideo && (
          <UYOVideo
            uyoVideo={uyoVideo}
            frameImage={frameImage}
            videoStyles={videoStyles}
            videoRef={videoRef}
          />
        )}
      </Button>
    </>
  );
};
